/**
 * Shared scene-update helpers used by SampledynagraphComponent and
 * SamplegraphdynanontimeComponent. The two components used to hold
 * byte-identical copies of updateScenes2..updateScenes10 (~5,500 lines
 * of duplication). The bodies have been moved here as free functions
 * with an explicit `this: DynaSceneContext` parameter so both hosts can
 * delegate via updateScenesN.call(this, ...).
 *
 * The functions mutate `draggedData` in place and read from the caller''s
 * `data` / `changedPoint` / `getDynaTimeXRefernce`.
 */

export interface DynaSceneContext {
  data: LegacyAny[];
  changedPoint: LegacyAny;
  getDynaTimeXRefernce(dynaModX: number): string;
}

/**
 * Push a config point mirroring `data[timeIdx]` (time), `data[outputIdx]`
 * (dim level), and `data[zfadeIdx]` (fade time). The three indices are
 * usually equal; some branches read them from different rows.
 */
function pushPoint(
  data: LegacyAny[],
  draggedData: LegacyAny,
  ctx: DynaSceneContext,
  timeIdx: number,
  outputIdx: number,
  zfadeIdx: number
): void {
  draggedData.push({
    dyanXTime: ctx.getDynaTimeXRefernce(parseFloat(data[timeIdx].dyanXTime)),
    dyanYOutput: parseInt(data[outputIdx].dyanYOutput),
    dynaZfadeTime: parseInt(data[zfadeIdx].dynaZfadeTime),
  });
}

/**
 * Push a config point with an explicit dyanYOutput override (used when the
 * dragged output value from another row must be propagated).
 */
function pushPointY(
  data: LegacyAny[],
  draggedData: LegacyAny,
  ctx: DynaSceneContext,
  timeIdx: number,
  yOutput: number,
  zfadeIdx: number
): void {
  draggedData.push({
    dyanXTime: ctx.getDynaTimeXRefernce(parseFloat(data[timeIdx].dyanXTime)),
    dyanYOutput: yOutput,
    dynaZfadeTime: parseInt(data[zfadeIdx].dynaZfadeTime),
  });
}

/**
 * Push a config point with a pre-computed time value (typically the
 * `modifiedOutput` local variable that holds a parseFloat of a dragged
 * dyanXTime). Used by the "time changed" branches.
 */
function pushPointTime(
  data: LegacyAny[],
  draggedData: LegacyAny,
  ctx: DynaSceneContext,
  timeValue: number,
  outputIdx: number,
  zfadeIdx: number
): void {
  draggedData.push({
    dyanXTime: ctx.getDynaTimeXRefernce(timeValue),
    dyanYOutput: parseInt(data[outputIdx].dyanYOutput),
    dynaZfadeTime: parseInt(data[zfadeIdx].dynaZfadeTime),
  });
}

/**
 * Pushes the five standard scene data-points (indices 2, 4, 6, 8, 10)
 * that are a shared prefix in all changedPoint ≥ 11 branches of
 * updateScenes6 through updateScenes10.
 */
function pushBaseFiveScenes(
  data: LegacyAny[],
  draggedData: LegacyAny,
  ctx: DynaSceneContext
): void {
  pushPoint(data, draggedData, ctx, 2, 2, 2);
  pushPoint(data, draggedData, ctx, 4, 4, 4);
  pushPoint(data, draggedData, ctx, 6, 6, 6);
  pushPoint(data, draggedData, ctx, 8, 8, 8);
  pushPoint(data, draggedData, ctx, 10, 10, 10);
}

/**
 * Generic handler for changedPoints 11..maxCp used by updateScenes7–10.
 * Eliminates duplicated if-chains by computing the push sequence from the
 * changedPoint value and maxCp (14 for scenes7, 16 for scenes8, etc.).
 */
function handleExtendedScenePoint(
  ctx: DynaSceneContext,
  draggedData: LegacyAny,
  maxCp: number
): void {
  const cp = ctx.changedPoint;
  if (cp < 11 || cp > maxCp) {
    return;
  }

  if (cp === 11) {
    pushBaseFiveScenes(ctx.data, draggedData, ctx);
    pushPoint(ctx.data, draggedData, ctx, 11, 12, 11);
    return;
  }

  if (cp === 12) {
    pushBaseFiveScenes(ctx.data, draggedData, ctx);
    pushPoint(ctx.data, draggedData, ctx, 11, 11, 12);
    return;
  }

  // Odd changedPoints (13, 15, 17, 19): time changes
  if (cp % 2 === 1) {
    pushBaseFiveScenes(ctx.data, draggedData, ctx);
    for (let i = 11; i <= cp; i += 2) {
      pushPoint(ctx.data, draggedData, ctx, i, i + 1, i);
    }
    return;
  }

  // Even changedPoints (14, 16, 18, 20): output-only changes
  const modifiedOutput = parseInt(ctx.data[cp].dyanYOutput);
  pushBaseFiveScenes(ctx.data, draggedData, ctx);
  for (let i = 12; i <= cp - 2; i += 2) {
    pushPoint(ctx.data, draggedData, ctx, i, i, i);
  }
  pushPointY(ctx.data, draggedData, ctx, cp, modifiedOutput, cp);
}

/**
 * Handles changedPoints 0–10 which are identical across updateScenes5
 * through updateScenes10.  Returns true when the point was handled so
 * callers can short-circuit their own if-chains.
 */
function handleBaseSceneChanges(
  this: DynaSceneContext,
  draggedData: LegacyAny
): boolean {
  switch (this.changedPoint) {
    case 0: {
      const v = parseInt(this.data[0].dyanYOutput);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPointY(this.data, draggedData, this, 10, v, 10);
      return true;
    }
    case 1: {
      const v = parseFloat(this.data[1].dyanXTime);
      pushPointTime(this.data, draggedData, this, v, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 2: {
      const v = parseInt(this.data[2].dyanYOutput);
      pushPointY(this.data, draggedData, this, 1, v, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 3: {
      const v = parseFloat(this.data[3].dyanXTime);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPointTime(this.data, draggedData, this, v, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 4: {
      const v = parseInt(this.data[4].dyanYOutput);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPointY(this.data, draggedData, this, 3, v, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 5: {
      const v = parseFloat(this.data[5].dyanXTime);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPointTime(this.data, draggedData, this, v, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 6: {
      const v = parseInt(this.data[6].dyanYOutput);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPointY(this.data, draggedData, this, 5, v, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 7: {
      const v = parseFloat(this.data[7].dyanXTime);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPointTime(this.data, draggedData, this, v, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 8: {
      const v = parseInt(this.data[8].dyanYOutput);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPointY(this.data, draggedData, this, 7, v, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      return true;
    }
    case 9: {
      const v = parseFloat(this.data[9].dyanXTime);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPointTime(this.data, draggedData, this, v, 10, 10);
      return true;
    }
    case 10: {
      const v = parseInt(this.data[10].dyanYOutput);
      pushPoint(this.data, draggedData, this, 2, 2, 2);
      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPointY(this.data, draggedData, this, 9, v, 10);
      return true;
    }
    default:
      return false;
  }
}

export function updateScenes10(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
  if (handleBaseSceneChanges.call(this, draggedData)) {
    return;
  }
  handleExtendedScenePoint(this, draggedData, 20);
}

export function updateScenes9(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
  if (handleBaseSceneChanges.call(this, draggedData)) {
    return;
  }
  handleExtendedScenePoint(this, draggedData, 18);
}

export function updateScenes8(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
  if (handleBaseSceneChanges.call(this, draggedData)) {
    return;
  }
  handleExtendedScenePoint(this, draggedData, 16);
}

export function updateScenes7(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
  if (handleBaseSceneChanges.call(this, draggedData)) {
    return;
  }
  handleExtendedScenePoint(this, draggedData, 14);
}

export function updateScenes6(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
  if (handleBaseSceneChanges.call(this, draggedData)) {
    return;
  }
  if (this.changedPoint === 11) {

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      pushPoint(this.data, draggedData, this, 11, 12, 11);
    }

    if (this.changedPoint === 12) {
      //output Only
      const modifiedOutput = parseInt(this.data[12].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPoint(this.data, draggedData, this, 10, 10, 10);
      pushPointY(this.data, draggedData, this, 11, modifiedOutput, 12);
    }
    if (this.changedPoint === 13) {
      //output Only
      const modifiedOutput = parseInt(this.data[13].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPointY(this.data, draggedData, this, 10, modifiedOutput, 10);
      pushPointY(this.data, draggedData, this, 12, modifiedOutput, 12);
    }
  }

export function updateScenes5(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
  if (handleBaseSceneChanges.call(this, draggedData)) {
    return;
  }
  if (this.changedPoint === 11) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[11].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
      pushPointY(this.data, draggedData, this, 10, modifiedOutput, 10);
    }
  }

export function updateScenes4(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
    if (this.changedPoint === 0) {
      //OUTPUT
      const modifiedOutput = parseInt(this.data[0].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPointY(this.data, draggedData, this, 8, modifiedOutput, 8);
    }
    if (this.changedPoint === 1) {
      //TIME
      const modifiedOutput = parseFloat(this.data[1].dyanXTime);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
    }
    if (this.changedPoint === 2) {
      //OUTPUT
      const modifiedOutput = parseInt(this.data[2].dyanYOutput);

      pushPointY(this.data, draggedData, this, 1, modifiedOutput, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
    }
    if (this.changedPoint === 3) {
      //TIME ONLY
      const modifiedOutput = parseFloat(this.data[3].dyanXTime);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);
      pushPoint(this.data, draggedData, this, 8, 8, 8);
    }

    if (this.changedPoint === 4) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[4].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPointY(this.data, draggedData, this, 3, modifiedOutput, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
    }
    if (this.changedPoint === 5) {
      //TIME ONLY
      const modifiedOutput = parseFloat(this.data[5].dyanXTime);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 6, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
    }
    if (this.changedPoint === 6) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[6].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPointY(this.data, draggedData, this, 5, modifiedOutput, 6);

      pushPoint(this.data, draggedData, this, 8, 8, 8);
    }
    if (this.changedPoint === 7) {
      //TIME ONLY
      const modifiedOutput = parseFloat(this.data[7].dyanXTime);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 8, 8);
    }
    if (this.changedPoint === 8) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[8].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);

      pushPointY(this.data, draggedData, this, 7, modifiedOutput, 8);
    }
    if (this.changedPoint === 9) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[9].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 5, 6, 6);

      pushPointY(this.data, draggedData, this, 7, modifiedOutput, 8);
    }
  }

export function updateScenes3(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
    if (this.changedPoint === 0) {
      //OUTPUT
      const modifiedOutput = parseInt(this.data[0].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPointY(this.data, draggedData, this, 6, modifiedOutput, 6);
    }
    if (this.changedPoint === 1) {
      //TIME
      const modifiedOutput = parseFloat(this.data[1].dyanXTime);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);
      pushPoint(this.data, draggedData, this, 6, 6, 6);
    }
    if (this.changedPoint === 2) {
      //OUTPUT
      const modifiedOutput = parseInt(this.data[2].dyanYOutput);

      pushPointY(this.data, draggedData, this, 1, modifiedOutput, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);
    }
    if (this.changedPoint === 3) {
      //TIME ONLY
      const modifiedOutput = parseFloat(this.data[3].dyanXTime);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 4, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);
    }

    if (this.changedPoint === 4) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[4].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPointY(this.data, draggedData, this, 3, modifiedOutput, 4);

      pushPoint(this.data, draggedData, this, 6, 6, 6);
    }
    if (this.changedPoint === 5) {
      //TIME ONLY
      const modifiedOutput = parseFloat(this.data[5].dyanXTime);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPointTime(this.data, draggedData, this, modifiedOutput, 6, 6);
    }
    if (this.changedPoint === 6) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[6].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPointY(this.data, draggedData, this, 5, modifiedOutput, 6);
    }
    if (this.changedPoint === 7) {
      //OUTPUT ONLY - Chnage Scene 2
      const modifiedOutput = parseInt(this.data[7].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);

      pushPointY(this.data, draggedData, this, 5, modifiedOutput, 6);
    }
  }

export function updateScenes2(this: DynaSceneContext, data: LegacyAny[], draggedData: LegacyAny) {
    if (this.changedPoint === 0) {
      const modifiedOutput = parseInt(this.data[0].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPointY(this.data, draggedData, this, 4, modifiedOutput, 4);
    }
    if (this.changedPoint === 1) {
      const modifiedOutput = parseInt(this.data[1].dyanYOutput);

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPointY(this.data, draggedData, this, 4, modifiedOutput, 4);
    }
    if (this.changedPoint === 2) {
      pushPoint(this.data, draggedData, this, 2, 3, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);
    }
    if (this.changedPoint === 3) {
      //OUTPUT ONLY
      pushPoint(this.data, draggedData, this, 2, 3, 2);

      pushPoint(this.data, draggedData, this, 4, 4, 4);
    }

    if (this.changedPoint === 4) {
      //TIME ONLY - Chnage Scene 2
      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 4, 5, 4);
    }
    if (this.changedPoint === 5) {
      //OUTPUT ONLY - Chnage Scene 2

      pushPoint(this.data, draggedData, this, 2, 2, 2);

      pushPoint(this.data, draggedData, this, 3, 5, 3);
    }
  }
